import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProgramNode } from '../../../common/models/care-monitor/program-node';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { ProgramLog } from '@meplis/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'mec-program-open-text',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './program-open-text.component.html',
  styleUrls: ['./program-open-text.component.scss'],
})
export class ProgramOpenTextComponent implements OnDestroy, OnChanges {
  @Input() public programNode: ProgramNode;
  @Output() public onSelectItemChange = new EventEmitter<{
    programLog: ProgramLog;
    valid: boolean;
  }>();

  public formArray: FormArray = new FormArray([]);
  private subscriptions: Subscription[] = [];

  constructor() {}

  ngOnDestroy(): void {
    this._clearSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.programNode) return;

    this._clearSubscriptions();
    this.formArray.clear();

    const mapped =
      this.programNode?.log?.results?.map((r) => String(r.value)) ?? [];
    const initialValues: string[] = mapped.length > 0 ? mapped : [''];

    const valuesToCreate = this.programNode.multiple
      ? initialValues
      : [initialValues[0] ?? ''];

    valuesToCreate.forEach((val) => this._createControl(val));

    this._emitProgramLog();
  }

  public getFormControl(control: AbstractControl): FormControl {
    return control as FormControl;
  }

  public addMore(): void {
    this._createControl('');
    this._emitProgramLog();
  }

  public remove(index: number): void {
    const sub = this.subscriptions[index];
    if (sub) {
      sub.unsubscribe();
    }
    this.subscriptions.splice(index, 1);
    this.formArray.removeAt(index);
    this._emitProgramLog();
  }

  private _createControl(value: string): void {
    const validators = this._buildValidators();
    const control = new FormControl(value ?? '', validators);
    this.formArray.push(control);

    const sub = control.valueChanges
      .pipe(debounceTime(800), distinctUntilChanged())
      .subscribe(() => {
        this._emitProgramLog();
      });
    this.subscriptions.push(sub);
  }

  private _buildValidators(): any[] {
    const v = [];
    if (this.programNode?.validation) {
      if (this.programNode.required) {
        v.push(Validators.required);
      }
      if (this.programNode.validation.minLength) {
        v.push(Validators.minLength(this.programNode.validation.minLength));
      }
      if (this.programNode.validation.maxLength) {
        v.push(Validators.maxLength(this.programNode.validation.maxLength));
      }
    }
    return v;
  }

  private _emitProgramLog(): void {
    const results = this.formArray.controls
      .map((ctrl) => {
        if (ctrl.value && ctrl.value !== '') {
          return {
            name: this.programNode.variable.name,
            value: ctrl.value,
          };
        }
      })
      .filter((r) => r !== undefined);

    const log: ProgramLog = {
      to: this.programNode.id,
      at: new Date().toISOString(),
      results,
    };

    const valid = this.formArray.valid;
    this.onSelectItemChange.emit({ programLog: log, valid });
  }

  private _clearSubscriptions(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }
}
